/* ===========================================================================
   ReadyWise - cart drawer
   ---------------------------------------------------------------------------
   History:

   v1 (2026-09-12) fixed the "cart is empty" bug. The drawer used to re-render
   on a fixed setTimeout(..., 800) unrelated to the /cart/add.js request, so any
   add slower than 800ms rendered the PRE-add cart. Opening the cart icon only
   toggled a CSS class, so a stale drawer stayed stale until a page reload.

   v2 (this file) fixes the gap that fix exposed. The drawer opens instantly on
   tap - which is right, the tap must feel responsive - but the contents cannot
   arrive until /cart/add.js finishes. That left the empty-cart message on
   screen for a beat, which reads as "my cart is empty" rather than "loading".
   The drawer now shows an explicit loading state instead, and in-place updates
   (quantity, remove) dim rather than blank out.

   Measured breakdown of a single add on the live site, click to populated:

     0ms      tap
     4ms      Candyrack /api/candyrack/offers starts
     322ms    Candyrack returns
     1652ms   POST /cart/add.js finally starts   <- ~1.2s spent before the add
     2237ms   add completes
     2650ms   GET /?section_id=cart-drawer starts
     2871ms   section returns, drawer populated

   Only the last ~400ms of that is this file's to give back. The 1.2s before the
   add fires is Candyrack gating add-to-cart on its upsell lookup, and is an app
   configuration question, not a theme one.
   =========================================================================== */

(function () {
  'use strict';

  var DRAWER = '.cart-drawer';
  var MUTATING = /\/cart\/(add|update|change|clear)(\.js|\.json)?(\?|$)/;
  var LOADING_CLASS = 'cart-drawer--loading';
  var BUSY_CLASS = 'cart-drawer--busy';
  var SAFETY_MS = 10000;

  /* --- loading / busy styling -------------------------------------------
     Injected here so the whole fix stays in one file. Everything is driven by
     pseudo-elements and visibility, so nothing needs re-injecting after the
     drawer's innerHTML is replaced.                                        */

  function injectStyles() {
    if (document.getElementById('cart-drawer-state-styles')) return;
    var css = [
      '.cart-drawer--loading .cart-drawer-box,',
      '.cart-drawer--busy .cart-drawer-box { position: relative; }',

      /* Loading: contents are unknown, so hide them rather than assert "empty". */
      '.cart-drawer--loading .empty-cart,',
      '.cart-drawer--loading .cart-drawer-items,',
      '.cart-drawer--loading .cart-drawer-section-item,',
      '.cart-drawer--loading .cart-drawer-footer,',
      '.cart-drawer--loading .cart-drawer__gamification { visibility: hidden; }',

      '.cart-drawer--loading .cart-drawer-box::after {',
      '  content: ""; position: absolute; top: 50%; left: 50%;',
      '  width: 34px; height: 34px; margin: -17px 0 0 -17px;',
      '  border: 3px solid rgba(0,0,0,.13);',
      '  border-top-color: rgba(0,0,0,.5);',
      '  border-radius: 50%; z-index: 5; pointer-events: none;',
      '  animation: cartDrawerSpin .7s linear infinite;',
      '}',

      /* Busy: contents are known and still on screen, just going stale. */
      '.cart-drawer--busy .cart-drawer-form { opacity: .45; transition: opacity .12s ease; }',
      '.cart-drawer--busy .cart-drawer-box { pointer-events: none; }',
      '.cart-drawer--busy .cart-drawer-header-right-close { pointer-events: auto; }',

      '@keyframes cartDrawerSpin { to { transform: rotate(360deg); } }',

      '@media (prefers-reduced-motion: reduce) {',
      '  .cart-drawer--loading .cart-drawer-box::after { animation-duration: 2s; }',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cart-drawer-state-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* --- open / close ------------------------------------------------------ */

  function openCartDrawer() {
    var d = document.querySelector(DRAWER);
    if (!d) return;
    d.classList.add('cart-drawer--active');
    document.body.classList.add('cart-drawer--visible');
  }

  function closeCartDrawer() {
    var d = document.querySelector(DRAWER);
    if (!d) return;
    d.classList.remove('cart-drawer--active');
    document.body.classList.remove('cart-drawer--visible');
    setState(null);
  }

  function updateCartItemCounts(count) {
    document.querySelectorAll('.CartCount').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* --- loading / busy state --------------------------------------------- */

  var safetyTimer = null;

  function setState(state) {
    var d = document.querySelector(DRAWER);
    if (!d) return;

    d.classList.remove(LOADING_CLASS, BUSY_CLASS);
    if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }

    if (!state) return;

    d.classList.add(state === 'busy' ? BUSY_CLASS : LOADING_CLASS);

    if (state === 'loading') {
      // The header still says "Your Cart (0)" from the pre-add render. Drop the
      // stale count while we don't know it; the next render restores it.
      var h2 = d.querySelector('.cart-drawer-header h2');
      if (h2) h2.textContent = h2.textContent.replace(/\s*\(\s*\d+\s*\)\s*$/, '');
    }

    // Never let a failed request strand the drawer in a spinner.
    safetyTimer = setTimeout(function () {
      var el = document.querySelector(DRAWER);
      if (el) el.classList.remove(LOADING_CLASS, BUSY_CLASS);
      safetyTimer = null;
    }, SAFETY_MS);
  }

  /* --- render ------------------------------------------------------------
     Sequenced so a slow response can never overwrite a newer one, and
     de-duplicated so a burst of cart events costs one render.             */

  var renderSeq = 0;
  var pendingRender = null;

  function updateCartDrawer() {
    var seq = ++renderSeq;

    return Promise.all([
      fetch('/?section_id=cart-drawer', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (r) {
        if (!r.ok) throw new Error('section ' + r.status);
        return r.text();
      }),
      fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; })
    ]).then(function (results) {
      // A newer render started while this one was in flight - discard.
      if (seq !== renderSeq) return;

      var wrapper = document.createElement('div');
      wrapper.innerHTML = results[0];

      var fresh = wrapper.querySelector(DRAWER);
      var current = document.querySelector(DRAWER);
      if (!fresh || !current) return;

      current.innerHTML = fresh.innerHTML;

      if (results[1] && typeof results[1].item_count === 'number') {
        updateCartItemCounts(results[1].item_count);
      }

      document.dispatchEvent(new CustomEvent('cart-drawer:rendered'));
    }).catch(function (err) {
      if (window.console && console.warn) console.warn('[cart-drawer] refresh failed:', err);
    }).then(function () {
      // Clear the spinner whether we rendered, were superseded, or failed.
      if (seq === renderSeq) setState(null);
    });
  }

  // Coalesce rapid-fire cart events (the add, plus the app-triggered cart reads
  // that follow it) into a single render. Kept short - this sits directly in
  // the user-visible latency.
  function scheduleRender() {
    if (pendingRender) return pendingRender;
    pendingRender = new Promise(function (resolve) {
      setTimeout(function () {
        pendingRender = null;
        updateCartDrawer().then(resolve);
      }, 16);
    });
    return pendingRender;
  }

  /* --- detect cart mutations --------------------------------------------
     The add itself is performed by the theme's product form and by apps
     (Candyrack, bundles, ...). Instead of guessing how long that takes, we
     watch the network and react when it genuinely finishes.               */

  if (!window.__cartDrawerNetworkHooked) {
    window.__cartDrawerNetworkHooked = true;

    var announce = function () {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    };

    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var first = args[0];
      var url = typeof first === 'string' ? first : (first && first.url) || '';
      var promise = origFetch.apply(this, args);
      if (MUTATING.test(url)) {
        promise.then(function (r) { if (r && r.ok) announce(); }, function () {});
      }
      return promise;
    };

    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cartDrawerUrl = url;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (MUTATING.test(this.__cartDrawerUrl || '')) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) announce();
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  document.addEventListener('cart:updated', function () { scheduleRender(); });

  /* --- delegated drawer interactions ------------------------------------ */

  function lineKey(node) {
    var row = node.closest('[data-line-item-key]');
    return row ? row.getAttribute('data-line-item-key') : null;
  }

  function changeLine(key, quantity) {
    setState('busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') updateCartItemCounts(cart.item_count);
        return scheduleRender();
      })
      .catch(function () { setState(null); });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // Open the drawer from the header cart icon - and always re-sync it.
    // Existing contents stay visible and dim while it refreshes, rather than
    // blanking out, because in this path what's on screen is usually right.
    var opener = t.closest('.openCartDrawer');
    if (opener) {
      e.preventDefault();
      openCartDrawer();
      setState('busy');
      scheduleRender();
      return;
    }

    if (t.closest('.cart-drawer-header-right-close')) {
      e.preventDefault();
      closeCartDrawer();
      return;
    }
    if (t.closest(DRAWER) && !t.closest('.cart-drawer-box')) {
      closeCartDrawer();
      return;
    }

    var remove = t.closest('.remove__item');
    if (remove) {
      e.preventDefault();
      var rk = lineKey(remove);
      if (rk) changeLine(rk, 0);
      return;
    }

    var qty = t.closest('.cart-drawer-quantity-selectors');
    if (qty) {
      e.preventDefault();
      var qk = lineKey(qty);
      var sel = qty.closest('.cart-drawer-quantity-selector');
      var input = sel && sel.querySelector('input');
      if (!input) {
        var row = qty.closest('[data-line-item-key]');
        input = row && row.querySelector('input');
      }
      if (!qk || !input) return;
      var currentQty = Number(input.value) || 0;
      var next = qty.classList.contains('cart-drawer-quantity-selector-plus')
        ? currentQty + 1
        : currentQty - 1;
      if (next < 0) next = 0;
      changeLine(qk, next);
      return;
    }

    var xsell = t.closest('.cart-x-sell-item-add');
    if (xsell) {
      e.preventDefault();
      var variantId = xsell.getAttribute('data-variant-id');
      if (!variantId) return;
      xsell.setAttribute('disabled', 'disabled');
      setState('busy');
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function () { return scheduleRender(); })
        .catch(function () { setState(null); })
        .then(function () { xsell.removeAttribute('disabled'); });
      return;
    }

    // Add to cart, from anywhere on the site.
    // We do NOT preventDefault and we do NOT guess a delay - the theme's own
    // form handler performs the add, and the network hook above fires
    // 'cart:updated' the moment it really completes. Until then the drawer
    // shows a spinner rather than the empty-cart message, because at this
    // point we genuinely do not know what the cart contains.
    if (t.closest('.js-product-add-to-cart-button') || t.closest('.Button-Add')) {
      openCartDrawer();
      setState('loading');
    }
  }, true);

  // Keep the drawer honest if the page is restored from the back/forward cache.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scheduleRender();
  });

  injectStyles();

  // Expose the same globals the rest of the theme already calls.
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  window.updateCartDrawer = updateCartDrawer;
  window.updateCartItemCounts = updateCartItemCounts;
})();
